import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import {
  CheckCircle2,
  XCircle,
  Package,
  RefreshCw,
  Filter,
} from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

const STATUS_TONE = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected: "bg-rose-100 text-rose-800 border-rose-200",
};

const STATUS_LABEL = {
  pending: "Pendente",
  approved: "Aprovado",
  rejected: "Rejeitado",
};

export default function StockConsumptions() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null); // consumption row
  const [reviewMode, setReviewMode] = useState("approve"); // approve | reject
  const [reviewForm, setReviewForm] = useState({ adjusted_qty: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const [stockTotals, setStockTotals] = useState({}); // name -> {total, lots}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/sanitary/consumptions?limit=300`);
      setItems(data);
      try {
        const { data: prods } = await api.get("/sanitary/products");
        const t = {};
        for (const p of prods) {
          const entry = { total: Number(p.quantity) || 0, lots: p.lots_count || 0 };
          t[p.id] = entry;
          t[p.name] = entry;
        }
        setStockTotals(t);
      } catch (err) {
        console.debug("stock totals load failed (informativo)", err);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao carregar");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((x) => x.status === filter)),
    [items, filter]
  );

  const summary = useMemo(() => {
    const s = { pending: 0, approved: 0, rejected: 0 };
    for (const x of items) s[x.status] = (s[x.status] || 0) + 1;
    return s;
  }, [items]);

  const openReview = (row, mode) => {
    setReviewing(row);
    setReviewMode(mode);
    setReviewForm({ adjusted_qty: row.requested_qty, notes: "" });
  };

  const submitReview = async () => {
    if (!reviewing) return;
    setBusy(true);
    try {
      const body = {
        adjusted_qty:
          reviewMode === "approve" && Number(reviewForm.adjusted_qty) !== reviewing.requested_qty
            ? Number(reviewForm.adjusted_qty)
            : null,
        notes: reviewForm.notes,
      };
      const endpoint =
        reviewMode === "approve"
          ? `/sanitary/consumptions/${reviewing.id}/approve`
          : `/sanitary/consumptions/${reviewing.id}/reject`;
      await api.post(endpoint, body);
      toast.success(reviewMode === "approve" ? "Aprovado — estoque atualizado" : "Rejeitado");
      setReviewing(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Layout
      title="Consumos de Estoque"
      subtitle="Aprove ou ajuste consumos capturados via checklist"
      actions={
        <Button
          variant="outline"
          onClick={load}
          className="border-stone-300 bg-white"
          data-testid="stock-consumptions-refresh"
        >
          <RefreshCw className="h-4 w-4 mr-2" /> Atualizar
        </Button>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
        {[
          { key: "pending", label: "Pendentes", tone: "border-amber-200 bg-amber-50 text-amber-900" },
          { key: "approved", label: "Aprovados", tone: "border-emerald-200 bg-emerald-50 text-emerald-900" },
          { key: "rejected", label: "Rejeitados", tone: "border-rose-200 bg-rose-50 text-rose-900" },
        ].map((k) => (
          <Card
            key={k.key}
            className={`p-4 rounded-xl border ${k.tone}`}
            data-testid={`stock-consumptions-summary-${k.key}`}
          >
            <div className="text-[10px] uppercase tracking-[0.18em] font-body opacity-80">{k.label}</div>
            <div className="font-display text-3xl font-extrabold mt-1">{summary[k.key] || 0}</div>
          </Card>
        ))}
      </div>

      <Card className="p-3 rounded-xl border-stone-200 bg-white mb-4 flex items-center gap-2">
        <Filter className="h-4 w-4 text-stone-500" />
        <span className="text-xs font-body text-stone-600 mr-2">Filtro:</span>
        {["pending", "approved", "rejected", "all"].map((k) => (
          <Button
            key={k}
            size="sm"
            variant={filter === k ? "default" : "outline"}
            onClick={() => setFilter(k)}
            className={
              filter === k
                ? "bg-emerald-800 hover:bg-emerald-900 text-white"
                : "bg-white"
            }
            data-testid={`stock-consumptions-filter-${k}`}
          >
            {k === "all" ? "Todos" : STATUS_LABEL[k]}
          </Button>
        ))}
      </Card>

      <Card
        className="rounded-xl border-stone-200 bg-white overflow-hidden"
        data-testid="stock-consumptions-table"
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead>Item</TableHead>
              <TableHead>Consumo</TableHead>
              <TableHead>Origem</TableHead>
              <TableHead>Executor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-stone-500 py-10 font-body text-sm">
                  Carregando…
                </TableCell>
              </TableRow>
            )}
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-stone-500 py-10">
                  <Package className="h-6 w-6 mx-auto mb-2 text-stone-400" />
                  Nenhum consumo neste filtro
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              filtered.map((r) => {
                const isIn = (r.operation || "out") === "in";
                return (
                <TableRow key={r.id} data-testid={`stock-consumption-row-${r.id}`}>
                  <TableCell>
                    <div className="font-display font-semibold">{r.item_name}</div>
                    <div className="flex flex-wrap gap-1 mt-1">
                    <Badge
                      variant="outline"
                      className={
                        isIn
                          ? "border-emerald-300 text-emerald-800 bg-emerald-50 text-[10px]"
                          : "border-rose-300 text-rose-800 bg-rose-50 text-[10px]"
                      }
                    >
                      {isIn ? "Entrada (+)" : "Baixa (−)"}
                    </Badge>
                    {r.scanned && (
                      <Badge variant="outline" className="border-stone-300 text-stone-600 bg-white text-[10px]">
                        etiqueta lida
                      </Badge>
                    )}
                    {r.pvps_violation && (
                      <Badge className="bg-amber-200 text-amber-900 border-none text-[10px]" data-testid={`consumption-pvps-${r.id}`}>
                        fora da ordem PVPS
                      </Badge>
                    )}
                    </div>
                  </TableCell>
                  <TableCell className="font-body text-sm">
                    <div className="font-semibold">
                      {r.adjusted_qty ?? r.requested_qty} {r.unit}
                    </div>
                    {r.adjusted_qty != null && r.adjusted_qty !== r.requested_qty && (
                      <div className="text-[11px] text-stone-500">
                        Solicitado: {r.requested_qty} {r.unit}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="font-body text-xs text-stone-600">
                    {r.template_name || "—"}
                    <div className="text-[10px] text-stone-400">
                      {formatDateTime(r.created_at)}
                    </div>
                  </TableCell>
                  <TableCell className="font-body text-xs text-stone-700">
                    {r.operator_name || "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_TONE[r.status]}>
                      {STATUS_LABEL[r.status] || r.status}
                    </Badge>
                    {r.reviewer_name && (
                      <div className="text-[10px] text-stone-500 mt-0.5">por {r.reviewer_name}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.status === "pending" ? (
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          onClick={() => openReview(r, "approve")}
                          className="bg-emerald-700 hover:bg-emerald-800 text-white"
                          data-testid={`consumption-approve-${r.id}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Aprovar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openReview(r, "reject")}
                          className="border-rose-300 text-rose-700 hover:bg-rose-50"
                          data-testid={`consumption-reject-${r.id}`}
                        >
                          <XCircle className="h-3.5 w-3.5 mr-1" /> Rejeitar
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-stone-400 font-body italic">
                        {r.reviewer_notes || "—"}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Card>

      {/* Review dialog */}
      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              {reviewMode === "approve"
                ? reviewing?.operation === "in"
                  ? "Aprovar entrada"
                  : "Aprovar baixa"
                : "Rejeitar consumo"}
            </DialogTitle>
            <DialogDescription className="font-body text-xs text-stone-500">
              {reviewMode === "approve"
                ? reviewing?.operation === "in"
                  ? "Ajuste a quantidade se necessário. O estoque será somado imediatamente."
                  : "Ajuste a quantidade se necessário. O estoque será subtraído imediatamente."
                : "Registre o motivo. O saldo não será alterado."}
            </DialogDescription>
          </DialogHeader>
          {reviewing && (
            <div className="space-y-3">
              <div className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-sm font-body">
                <div className="font-semibold">{reviewing.item_name}</div>
                <div className="text-xs text-stone-600">
                  Solicitado por <b>{reviewing.operator_name}</b> em{" "}
                  {formatDateTime(reviewing.created_at)}
                </div>
                {reviewMode === "approve" &&
                  (reviewing.operation || "out") === "out" &&
                  (stockTotals[reviewing.product_id] || stockTotals[reviewing.item_name]) && (() => {
                    const t = stockTotals[reviewing.product_id] || stockTotals[reviewing.item_name];
                    return (
                    <div
                      className="text-xs text-emerald-800 mt-1 font-semibold"
                      data-testid="consumption-review-total-stock"
                    >
                      Estoque total disponível: {t.total} {reviewing.unit}
                      {t.lots > 1 &&
                        (reviewing.scanned
                          ? ` em ${t.lots} lotes — a baixa sai do lote da etiqueta lida${reviewing.pvps_violation ? "" : " (é o próximo PVPS)"}`
                          : ` em ${t.lots} lotes — a baixa segue a regra PVPS (vence primeiro, sai primeiro)`)}
                    </div>
                    );
                  })()}
                {reviewing.pvps_violation && (
                  <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 font-body" data-testid="consumption-review-pvps">
                    <b>Fora da ordem PVPS:</b> etiqueta do lote {reviewing.pvps_violation.scanned_batch || "s/ nº"} (val.{" "}
                    {reviewing.pvps_violation.scanned_expires_at ? String(reviewing.pvps_violation.scanned_expires_at).slice(0, 10).split("-").reverse().join("/") : "—"}) —
                    deveria usar o lote {reviewing.pvps_violation.expected_batch || "s/ nº"} (val.{" "}
                    {reviewing.pvps_violation.expected_expires_at ? String(reviewing.pvps_violation.expected_expires_at).slice(0, 10).split("-").reverse().join("/") : "—"}).
                    Uma ocorrência foi registrada.
                  </div>
                )}
              </div>
              {reviewMode === "approve" && (
                <div>
                  <Label>Quantidade a subtrair ({reviewing.unit})</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={reviewForm.adjusted_qty}
                    onChange={(e) =>
                      setReviewForm({ ...reviewForm, adjusted_qty: e.target.value })
                    }
                    className="mt-1 bg-white"
                    data-testid="consumption-review-qty"
                    autoFocus
                  />
                </div>
              )}
              <div>
                <Label>Notas (opcional)</Label>
                <Textarea
                  rows={2}
                  value={reviewForm.notes}
                  onChange={(e) =>
                    setReviewForm({ ...reviewForm, notes: e.target.value })
                  }
                  placeholder={
                    reviewMode === "approve"
                      ? "Ex: usado no turno da manhã"
                      : "Ex: item duplicado, valor incorreto"
                  }
                  className="mt-1 bg-white"
                  data-testid="consumption-review-notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              onClick={submitReview}
              disabled={busy}
              className={
                reviewMode === "approve"
                  ? "bg-emerald-700 hover:bg-emerald-800 text-white"
                  : "bg-rose-700 hover:bg-rose-800 text-white"
              }
              data-testid="consumption-review-submit"
            >
              {busy
                ? "Enviando…"
                : reviewMode === "approve"
                ? reviewing?.operation === "in"
                  ? "Aprovar e somar"
                  : "Aprovar e subtrair"
                : "Rejeitar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
